import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, MapPin, Calendar, Heart, MessageCircle, Image as ImageIcon, Mic, Film, AtSign } from "lucide-react";

function avatarColor(seed) {
  const palette = ["#ff6b35", "#4992ff", "#16a34a", "#a855f7", "#ec4899", "#0ea5e9", "#f59e0b", "#14b8a6"];
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="tk-card p-4" data-testid={`stat-card-${label}`}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "22" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>{label}</div>
          <div className="text-xl font-display font-semibold leading-tight">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function Istatistikler() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/journal/stats").then((r) => setStats(r.data)).catch(() => setStats(null)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="tk-card p-6 flex items-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
      </div>
    );
  }
  if (!stats || stats.total_posts === 0) {
    return (
      <div className="tk-card p-8 text-center text-sm" style={{ color: "hsl(var(--text-2))" }} data-testid="stats-empty">
        Henüz istatistik için yeterli gönderi yok. Birkaç anı paylaş, burası canlansın ✨
      </div>
    );
  }
  const maxUserPosts = Math.max(...stats.by_user.map((u) => u.post_count), 1);
  const maxLocCount = Math.max(...stats.by_location.map((u) => u.count), 1);
  const maxDayCount = Math.max(...stats.by_day.map((u) => u.count), 1);
  const maxTagged = Math.max(...stats.most_tagged.map((u) => u.count), 1);
  const totalMedia = (stats.media?.photo || 0) + (stats.media?.video || 0) + (stats.media?.audio || 0);

  return (
    <div className="space-y-4" data-testid="istatistikler-page">
      <div>
        <h2 className="text-lg font-display font-semibold">Yolculuk İstatistikleri</h2>
        <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
          Anılar üzerinden keşfedilen sayılar.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={ImageIcon} label="Anı" value={stats.total_posts} color="#4992ff" />
        <StatCard icon={Heart} label="Beğeni" value={stats.total_likes} color="#dc2626" />
        <StatCard icon={MessageCircle} label="Yorum" value={stats.total_comments} color="#16a34a" />
        <StatCard icon={Film} label="Medya" value={totalMedia} color="#a855f7" />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="tk-card p-2.5" data-testid="stats-photos">
          <ImageIcon className="w-4 h-4 mx-auto mb-0.5" style={{ color: "#4992ff" }} />
          <b>{stats.media?.photo || 0}</b> foto
        </div>
        <div className="tk-card p-2.5" data-testid="stats-videos">
          <Film className="w-4 h-4 mx-auto mb-0.5" style={{ color: "#a855f7" }} />
          <b>{stats.media?.video || 0}</b> video
        </div>
        <div className="tk-card p-2.5" data-testid="stats-audios">
          <Mic className="w-4 h-4 mx-auto mb-0.5" style={{ color: "#ff6b35" }} />
          <b>{stats.media?.audio || 0}</b> ses
        </div>
      </div>

      {/* By user */}
      {stats.by_user.length > 0 && (
        <div className="tk-card p-4" data-testid="stats-by-user">
          <div className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5">
            <ImageIcon className="w-4 h-4" style={{ color: "#4992ff" }} /> En çok paylaşan
          </div>
          <div className="space-y-2">
            {stats.by_user.slice(0, 8).map((u) => (
              <div key={u.username} className="flex items-center gap-3" data-testid={`stats-user-${u.username}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: avatarColor(u.username) }}>
                  {u.display_name?.[0] || u.username[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-medium truncate">{u.display_name}</span>
                    <span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>
                      {u.post_count} anı · {u.like_count} ❤ · {u.comment_count} 💬
                    </span>
                  </div>
                  <div className="mt-1"><Bar value={u.post_count} max={maxUserPosts} color="#4992ff" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By location */}
      {stats.by_location.length > 0 && (
        <div className="tk-card p-4" data-testid="stats-by-location">
          <div className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5">
            <MapPin className="w-4 h-4" style={{ color: "#ff6b35" }} /> En popüler konumlar
          </div>
          <div className="space-y-2">
            {stats.by_location.slice(0, 6).map((l) => (
              <div key={l.name} className="flex items-center gap-3" data-testid={`stats-loc-${l.name}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="truncate">{l.name}</span>
                    <span className="text-xs font-semibold">{l.count}</span>
                  </div>
                  <div className="mt-1"><Bar value={l.count} max={maxLocCount} color="#ff6b35" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By trip day */}
      {stats.by_day.length > 0 && (
        <div className="tk-card p-4" data-testid="stats-by-day">
          <div className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5">
            <Calendar className="w-4 h-4" style={{ color: "#7a4326" }} /> Günlere göre
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => {
              const found = stats.by_day.find((x) => x.trip_day === d);
              const count = found?.count || 0;
              const h = maxDayCount > 0 ? Math.max(8, (count / maxDayCount) * 70) : 8;
              return (
                <div key={d} className="flex flex-col items-center gap-1" data-testid={`stats-day-${d}`}>
                  <div className="text-xs font-semibold" style={{ color: count > 0 ? "#7a4326" : "hsl(var(--text-2))" }}>{count}</div>
                  <div className="w-full rounded-t-md" style={{ height: h, backgroundColor: count > 0 ? "#fb923c" : "hsl(var(--surface-2))" }} />
                  <div className="text-[10px]" style={{ color: "hsl(var(--text-2))" }}>G{d}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Most tagged */}
      {stats.most_tagged.length > 0 && (
        <div className="tk-card p-4" data-testid="stats-most-tagged">
          <div className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5">
            <AtSign className="w-4 h-4" style={{ color: "#a855f7" }} /> En çok etiketlenen
          </div>
          <div className="space-y-2">
            {stats.most_tagged.slice(0, 6).map((u) => (
              <div key={u.username} className="flex items-center gap-3" data-testid={`stats-tagged-${u.username}`}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: avatarColor(u.username) }}>
                  {u.display_name?.[0] || u.username[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-medium truncate">{u.display_name}</span>
                    <span className="text-xs font-semibold">{u.count}×</span>
                  </div>
                  <div className="mt-1"><Bar value={u.count} max={maxTagged} color="#a855f7" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
